import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useOutletContext, useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PedidosFiltersBar, PedidoListCard, PedidoStatusControl } from '@/components/Pedidos'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import type { PedidosLayoutOutletContext } from '@/layouts/PedidosLayout'
import { formatDateTime } from '@/lib/campaign-dashboard'
import {
  fetchCampaignSales,
} from '@/lib/campaign-reports'
import {
  getDefaultPedidosFilters,
  parsePedidosFiltersFromSearchParams,
  parseSearchQueryFromSearchParams,
  writePedidosFiltersToSearchParams,
  type PedidosListFiltersState,
} from '@/lib/pedidos-filters'
import type { CampaignSalesFilters } from '@/lib/campaign-reports'
import {
  downloadCampaignSalesPadraoExport,
  downloadCampaignSalesScpcExport,
  PEDIDOS_EXPORT_FORMAT_OPTIONS,
  type PedidosExportFormat,
} from '@/lib/pedidos-export'
import type { CampaignSaleListItem } from '@/types/campaign-report'
import { canChangePedidoStatus } from '@/lib/pedidos-permissions'
import { Select } from '@/components/Select'
import styles from './PedidosListaPage.module.css'

const PER_PAGE = 20

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function resolveTrackingSource(sale: CampaignSaleListItem): string {
  const last = sale.tracking?.last_touch?.utm_source?.trim()
  if (last) return last
  const first = sale.tracking?.first_touch?.utm_source?.trim()
  if (first) return first
  return 'direto'
}

function resolveTrackingCampaign(sale: CampaignSaleListItem): string {
  const last = sale.tracking?.last_touch?.utm_campaign?.trim()
  if (last) return last
  const first = sale.tracking?.first_touch?.utm_campaign?.trim()
  if (first) return first
  return '—'
}

function readInitialPage(): number {
  if (typeof window === 'undefined') return 1
  const p = parseInt(
    new URLSearchParams(window.location.search).get('page') ?? '1',
    10
  )
  return Number.isFinite(p) && p >= 1 ? p : 1
}

export function PedidosListaPage() {
  const { token, guard, isCollaborator } = useAuth()
  const { institutionId } = useInstitution()
  const [, setSearchParams] = useSearchParams()

  const { campaignId, campaignsLoading, hasCampaigns } =
    useOutletContext<PedidosLayoutOutletContext>()

  const [page, setPage] = useState(readInitialPage)
  const [filters, setFilters] = useState(() => {
    if (typeof window === 'undefined') return getDefaultPedidosFilters()
    return parsePedidosFiltersFromSearchParams(
      new URLSearchParams(window.location.search)
    )
  })
  const [searchInput, setSearchInput] = useState(() => {
    if (typeof window === 'undefined') return ''
    return parseSearchQueryFromSearchParams(
      new URLSearchParams(window.location.search)
    )
  })
  const [debouncedQ, setDebouncedQ] = useState(() => {
    if (typeof window === 'undefined') return ''
    return parseSearchQueryFromSearchParams(
      new URLSearchParams(window.location.search)
    ).trim()
  })

  const [sales, setSales] = useState<CampaignSaleListItem[]>([])
  const [pagination, setPagination] = useState<{
    current_page: number
    per_page: number
    total: number
    last_page: number
  } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [exportFormat, setExportFormat] = useState<PedidosExportFormat>('padrao')
  const previousCampaignIdRef = useRef<number | null>(null)
  const skipSearchPageResetOnce = useRef(true)

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = searchInput.trim()
      setDebouncedQ(next)
      if (skipSearchPageResetOnce.current) {
        skipSearchPageResetOnce.current = false
      } else {
        setPage(1)
      }
    }, 300)
    return () => window.clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    if (campaignId == null) return
    if (previousCampaignIdRef.current === null) {
      previousCampaignIdRef.current = campaignId
      return
    }
    if (previousCampaignIdRef.current === campaignId) return
    previousCampaignIdRef.current = campaignId
    setPage(1)
    setFilters(getDefaultPedidosFilters())
    setSearchInput('')
    setDebouncedQ('')
    skipSearchPageResetOnce.current = true
  }, [campaignId])

  const dateRangeValid = useMemo(
    () =>
      (filters.dateFrom === '' && filters.dateTo === '') ||
      (filters.dateFrom !== '' && filters.dateTo !== ''),
    [filters.dateFrom, filters.dateTo]
  )

  useEffect(() => {
    if (campaignId == null) return
    const next = writePedidosFiltersToSearchParams(
      filters,
      page,
      debouncedQ
    )
    setSearchParams(next, { replace: true })
  }, [campaignId, filters, page, debouncedQ, setSearchParams])

  const load = useCallback(async () => {
    if (campaignId == null) return
    if (!dateRangeValid) {
      setError('Informe data inicial e final juntas ou deixe ambas vazias.')
      setSales([])
      setPagination(null)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchCampaignSales(
        campaignId,
        {
          page,
          perPage: PER_PAGE,
          statuses: filters.statuses,
          dateFrom: filters.dateFrom || null,
          dateTo: filters.dateTo || null,
          dateBasis: filters.dateBasis,
          q: debouncedQ || null,
        },
        token ?? undefined
      )
      setSales(res.sales)
      setPagination(res.pagination)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erro ao carregar pedidos.'
      )
      setSales([])
      setPagination(null)
    } finally {
      setIsLoading(false)
    }
  }, [
    campaignId,
    page,
    token,
    filters,
    debouncedQ,
    dateRangeValid,
  ])

  useEffect(() => {
    if (campaignId == null || !hasCampaigns) return
    void load()
  }, [campaignId, load, hasCampaigns])

  const handleFiltersChange = (next: PedidosListFiltersState) => {
    setFilters(next)
    setPage(1)
  }

  const handleClearFilters = () => {
    setFilters(getDefaultPedidosFilters())
    setSearchInput('')
    setDebouncedQ('')
    setPage(1)
    skipSearchPageResetOnce.current = true
  }

  const exportFilters = useMemo<CampaignSalesFilters>(
    () => ({
      statuses: filters.statuses,
      dateFrom: filters.dateFrom || null,
      dateTo: filters.dateTo || null,
      dateBasis: filters.dateBasis,
      q: debouncedQ || null,
    }),
    [filters, debouncedQ]
  )

  const handleExport = async () => {
    if (campaignId == null || totalRegistros === 0 || exporting || !dateRangeValid) return
    setExporting(true)

    try {
      if (exportFormat === 'scpc') {
        await downloadCampaignSalesScpcExport(
          campaignId,
          exportFilters
        )
      } else {
        await downloadCampaignSalesPadraoExport(
          campaignId,
          exportFilters
        )
      }
    } catch {
      setError(
        exportFormat === 'scpc'
          ? 'Não foi possível exportar no formato SCPC. Tente novamente.'
          : 'Não foi possível exportar os pedidos. Tente novamente.'
      )
    } finally {
      setExporting(false)
    }
  }

  const handleSaleStatusChanged = useCallback(
    (paymentId: number, patch: {
      status: CampaignSaleListItem['status']
      confirmation_source?: CampaignSaleListItem['confirmation_source']
      is_manual_approval?: boolean
      allowed_status_transitions?: CampaignSaleListItem['allowed_status_transitions']
      status_changes?: CampaignSaleListItem['status_changes']
      paid_at?: CampaignSaleListItem['paid_at']
    }) => {
      setSales((current) =>
        current.map((sale) =>
          sale.payment_id === paymentId
            ? {
                ...sale,
                status: patch.status,
                confirmation_source: patch.confirmation_source,
                is_manual_approval: patch.is_manual_approval,
                allowed_status_transitions: patch.allowed_status_transitions,
                status_changes: patch.status_changes,
                paid_at: patch.paid_at ?? sale.paid_at,
              }
            : sale
        )
      )
    },
    []
  )

  const totalRegistros = pagination?.total ?? 0
  const isInitialLoad = isLoading && sales.length === 0 && error === null
  const isRefreshing = isLoading && !isInitialLoad

  if (guard === 'admin' && institutionId === null && !campaignsLoading) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:office-building-outline" className={styles.errorIcon} />
        Selecione uma instituição no topo para ver os pedidos.
      </div>
    )
  }

  if (!campaignsLoading && !hasCampaigns) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:cart-outline" className={styles.errorIcon} />
        Nenhuma campanha cadastrada. Crie uma campanha para ver pedidos.
      </div>
    )
  }

  if (campaignId == null) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        Carregando…
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.listHeader}>
        <div>
          <h2 className={styles.listTitle}>Lista de Pedidos</h2>
          <p className={styles.listSubtitle}>
            Lista de Pedidos ({totalRegistros})
          </p>
        </div>
        <nav className={styles.breadcrumb} aria-label="Navegação">
          <Link to="/home">Início</Link>
          <span className={styles.breadcrumbSep}>/</span>
          <span>Pedidos</span>
        </nav>
      </div>

      <PedidosFiltersBar
        value={filters}
        onChange={handleFiltersChange}
        onClear={handleClearFilters}
        searchQuery={searchInput}
        onSearchQueryChange={setSearchInput}
        isRefreshing={isRefreshing}
      />

      {!dateRangeValid ? (
        <p className={styles.validationBanner} role="alert">
          Preencha data inicial e final juntas ou limpe as duas datas.
        </p>
      ) : null}

      {isInitialLoad ? (
        <div className={styles.centered}>
          <Icon icon="mdi:loading" className={styles.spinner} />
          Carregando pedidos…
        </div>
      ) : error ? (
        <div className={styles.centered}>
          <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
          {error}
          <button type="button" className={styles.retryBtn} onClick={() => void load()}>
            Tentar novamente
          </button>
        </div>
      ) : (
        <>
          <div className={styles.tableCard} data-refreshing={isRefreshing ? 'true' : 'false'}>
            {isRefreshing ? (
              <div className={styles.tableRefreshingBar} aria-live="polite">
                <Icon icon="mdi:loading" className={styles.tableRefreshingIcon} />
                Atualizando pedidos…
              </div>
            ) : null}
            <div className={styles.desktopTableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.colId}>ID</th>
                    <th className={styles.colDates}>Datas</th>
                    <th className={styles.colClient}>Cliente</th>
                    <th className={`${styles.colEmail} ${styles.colOptional}`}>Email</th>
                    <th className={`${styles.colPhone} ${styles.colOptional}`}>Telefone</th>
                    <th className={styles.colTotal}>Total</th>
                    <th className={styles.colQty}>Qtd</th>
                    <th className={styles.colStatus}>Status</th>
                    <th className={`${styles.colOrigin} ${styles.colOptional}`}>Origem (ADS)</th>
                    <th className={styles.colActions}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.length === 0 ? (
                    <tr>
                      <td colSpan={10}>
                        <div className={styles.centered} style={{ padding: '2rem' }}>
                          Nenhum pedido encontrado com os filtros atuais.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sales.map((s) => (
                      <tr key={s.payment_id}>
                        <td className={styles.colId}>{s.payment_id}</td>
                        <td className={styles.colDates}>
                          <div className={styles.dateStack}>
                            {s.paid_at ? (
                              <span className={styles.dateLine}>
                                {formatDateTime(s.paid_at)}
                              </span>
                            ) : null}
                            <span
                              className={
                                s.paid_at ? styles.dateLineMuted : styles.dateLine
                              }
                            >
                              {formatDateTime(s.created_at)}
                            </span>
                          </div>
                        </td>
                        <td className={styles.colClient}>
                          <span className={styles.cellText} title={s.donor_name ?? undefined}>
                            {s.donor_name ?? '—'}
                          </span>
                        </td>
                        <td className={`${styles.colEmail} ${styles.colOptional}`}>
                          <span className={styles.cellText} title={s.email ?? undefined}>
                            {s.email ?? '—'}
                          </span>
                        </td>
                        <td className={`${styles.colPhone} ${styles.colOptional}`}>
                          <span className={styles.cellText} title={s.phone ?? undefined}>
                            {s.phone ?? '—'}
                          </span>
                        </td>
                        <td className={styles.colTotal}>{formatBrl(s.amount)}</td>
                        <td className={styles.colQty}>{s.quantity}</td>
                        <td className={styles.colStatus}>
                          {campaignId != null ? (
                            <PedidoStatusControl
                              campaignId={campaignId}
                              paymentId={s.payment_id}
                              currentStatus={s.status ?? 'pending'}
                              allowedTransitions={s.allowed_status_transitions}
                              confirmationSource={s.confirmation_source}
                              isManualApproval={s.is_manual_approval}
                              statusChanges={s.status_changes}
                              disabled={isRefreshing}
                              canChangeStatus={canChangePedidoStatus(
                                guard,
                                isCollaborator,
                                institutionId
                              )}
                              token={token}
                              onListItemChanged={(patch) =>
                                handleSaleStatusChanged(s.payment_id, patch)
                              }
                            />
                          ) : null}
                        </td>
                        <td className={`${styles.colOrigin} ${styles.colOptional}`}>
                          <div
                            className={styles.originStack}
                            title={`Campanha: ${resolveTrackingCampaign(s)}`}
                          >
                            <span className={styles.originBadge}>
                              <Icon icon="mdi:earth" />
                              {resolveTrackingSource(s)}
                            </span>
                            <span className={styles.originCampaignLabel}>
                              {resolveTrackingCampaign(s)}
                            </span>
                          </div>
                        </td>
                        <td className={styles.colActions}>
                          <div className={styles.actionsCell}>
                            <Link
                              to={`/pedidos/lista/pedido/${s.payment_id}`}
                              className={styles.actionView}
                              title="Ver detalhes do pedido"
                              aria-label="Ver detalhes do pedido"
                            >
                              <Icon icon="mdi:magnify" />
                            </Link>
                            <button
                              type="button"
                              className={styles.actionDanger}
                              title="Excluir (em breve)"
                              disabled
                              aria-disabled
                            >
                              <Icon icon="mdi:delete-outline" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.mobileCardsList}>
              {sales.length === 0 ? (
                <div className={styles.mobileEmpty}>
                  Nenhum pedido encontrado com os filtros atuais.
                </div>
              ) : (
                sales.map((s) => (
                  <PedidoListCard
                    key={s.payment_id}
                    sale={s}
                    campaignId={campaignId}
                    canChangeStatus={canChangePedidoStatus(
                      guard,
                      isCollaborator,
                      institutionId
                    )}
                    token={token}
                    disabled={isRefreshing}
                    trackingSource={resolveTrackingSource(s)}
                    trackingCampaign={resolveTrackingCampaign(s)}
                    onListItemChanged={(patch) =>
                      handleSaleStatusChanged(s.payment_id, patch)
                    }
                  />
                ))
              )}
            </div>
          </div>

          <div className={styles.footerRow}>
            {pagination && pagination.last_page > 1 ? (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </button>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                  Página {pagination.current_page} de {pagination.last_page}
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page >= pagination.last_page}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </button>
              </div>
            ) : (
              <span />
            )}
            <div className={styles.exportGroup}>
              <Select
                id="pedidos-export-format"
                className={styles.exportFormatSelect}
                label="Formato"
                value={exportFormat}
                onChange={(v) => setExportFormat(v as PedidosExportFormat)}
                options={PEDIDOS_EXPORT_FORMAT_OPTIONS}
                icon="mdi:file-delimited-outline"
              />
              <button
                type="button"
                className={styles.btnExport}
                onClick={() => void handleExport()}
                disabled={exporting || totalRegistros === 0 || !dateRangeValid}
              >
                <Icon icon="mdi:download" />
                {exporting ? 'Exportando...' : 'Exportar relatório'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
